import { Icon, IconButton } from '@smartgift/frontend-toolkit';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dateLabelFormatDayWithYear } from '@hero/constants';
import { Table, UserName } from '@hero/molecules';
import { useGetContacts, useGetTagList } from '@hero/services/contact-service/hooks';
import { formatDate } from '@hero/utils';

import * as S from './style';

const BasicTagContactsTable = ({ tags, disabledColumns = [], actionButtons = {} }) => {
  const { t } = useTranslation();

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [globalFilters, setGlobalFilters] = useState({});
  const { data: filterOptions } = useGetTagList();
  const { data: contacts, isLoading } = useGetContacts(
    {
      filters: {
        searchTerm: globalFilters.searchTerm,
        tags: globalFilters.filters?.map(item => item.value) || tags
      },
      pagination: {
        page: pageIndex + 1,
        limit: pageSize
      }
    },
    {
      enabled: tags?.length > 0,
      select(response) {
        if (tags.length === 0) {
          return {};
        }
        return response;
      }
    }
  );

  function firstNameCell({
    row: {
      original: { firstName, lastName }
    }
  }) {
    return <UserName firstName={firstName} lastName={lastName} hasNameAvatar />;
  }

  const columns = React.useMemo(
    () =>
      [
        {
          header: t('name'),
          size: 300,
          accessorKey: 'firstName',
          cell: firstNameCell
        },
        {
          id: 'email',
          header: t('email'),
          accessorKey: 'email'
        },
        {
          id: 'birthday',
          header: t('birthday'),
          accessorKey: 'birthday',
          cell: ({
            row: {
              original: { birthday }
            }
          }) => (
            <S.TableName role="button" tabIndex={0}>
              {formatDate(birthday, { format: dateLabelFormatDayWithYear, includeTime: false })}
            </S.TableName>
          )
        },
        {
          id: 'anniversary',
          header: t('workAnniversary'),
          accessorKey: 'anniversary',
          cell: ({
            row: {
              original: { anniversary }
            }
          }) => (
            <S.TableName role="button" tabIndex={0}>
              {formatDate(anniversary, { format: dateLabelFormatDayWithYear, includeTime: false })}
            </S.TableName>
          )
        },
        {
          id: 'tags',
          header: 'Tag',
          accessorFn: originalRow => {
            return originalRow?.tags?.map(tag => ({
              label: tag.name,
              value: tag.id
            }));
          },
          type: 'tags'
        },
        ...(actionButtons?.delete
          ? [
              {
                id: 'actions',
                header: '',
                size: 50,
                cell: ({ row }) => {
                  return (
                    <IconButton
                      variant="text"
                      color="fg"
                      onClick={() => actionButtons.delete.onClick(row?.original)}
                    >
                      <Icon name="delete" size="md" />
                    </IconButton>
                  );
                }
              }
            ]
          : [])
      ].filter(column => !disabledColumns.includes(column.id)),
    []
  );

  return (
    <Table
      columns={columns}
      data={contacts?.docs || []}
      globalFilters={globalFilters}
      pagination={{
        pageIndex,
        pageSize,
        total: contacts?.pagination?.total
      }}
      manualPagination
      loading={isLoading && contacts?.docs?.length > 0}
      setGlobalFilters={setGlobalFilters}
      setPagination={setPagination}
      pageCount={contacts?.pagination?.pages}
      enabled={{ filter: true, header: true }}
      rowHeight={52}
      filterProps={{
        placeholder: t('filterByType', { type: t('tags') }),
        inputPlaceholderOnMenu: t('searchTagOnFilterPlaceholder')
      }}
      filterOptions={filterOptions?.docs?.map(doc => ({ label: doc.name, value: doc.id })) || []}
      components={{
        SelectionHead: null,
        SecondHead: null
      }}
    />
  );
};

export { BasicTagContactsTable };
